import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { Link } from "react-router";

import type { CommunityFeedAnalyticsDto, MessageDetailV2Dto } from "@/api/types";
import { MessageSquare02Icon } from "@/components/Icons/MessageSquare02Icon";
import { Card } from "@/design-system/Card";
import { Divider } from "@/design-system/Divider";
import { Inline } from "@/design-system/Inline";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { Tooltip } from "@/design-system/Tooltip";
import { formatDistance } from "@/helpers/date";
import { getReactionImage, getReactionLabel, getTotalReactionAmount } from "@/helpers/reactions";
import { isDefined } from "@/helpers/util";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useSlug } from "@/hooks/useSlug";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { AnalyticsWidgetCard } from "../AnalyticsWidgetCard";

const FALLBACK_DATA: CommunityFeedAnalyticsDto = {
  lastUpdatedAt: new Date().toISOString(),
  totalResidentPostCount: 0,
};
export function CommunityFeedInsightsWidget(): React.ReactNode {
  const clockDate = useCurrentDate();
  const slug = useSlug();
  const { t } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const { data: communityFeedStats, isPending, isError, error } = useQuery(analyticsQueries.communityFeedStats());

  // If BE returns all data as null, this implies that the project does not yet have an active community feed.
  // Therefore we hide the widget
  if (
    !isError &&
    !isPending &&
    !isDefined(communityFeedStats?.totalResidentPostCount) &&
    !communityFeedStats?.mostReactedMessage &&
    !communityFeedStats?.mostCommentedMessage
  ) {
    return null;
  }

  const isDataAvailable =
    !!communityFeedStats &&
    Boolean(communityFeedStats.totalResidentPostCount) &&
    Boolean(communityFeedStats.mostReactedMessage) &&
    Boolean(communityFeedStats.mostCommentedMessage);
  const primaryData = isDataAvailable ? communityFeedStats : FALLBACK_DATA;

  return (
    <AnalyticsWidgetCard.Root
      title={t("component.analytics-widget.community-feed-insights.title")}
      description={t("component.analytics-widget.community-feed-insights.description")}
      isLoading={isPending}
      isError={isError}
      error={error}
      isDataAvailable={isDataAvailable}
      href={routes.communityFeed.list({
        slug,
      })}
    >
      <AnalyticsWidgetCard.Body align="stretch">
        <Stack gap="sm" align="center">
          {/* Residents post count */}
          <Card as="section">
            <Stack gap="xs">
              <span className="text-overline-emphasis text-text-secondary">
                {t("component.analytics-widget.community-feed-insights.resident-posts.label")}
              </span>
              <p className="text-body-emphasis">
                {t("component.analytics-widget.community-feed-insights.resident-posts.content", {
                  count: primaryData.totalResidentPostCount ?? 0,
                })}
              </p>
            </Stack>
          </Card>

          {/* Most reacted post */}
          <Card
            as={Link}
            to={routes.communityFeed.postDetails({
              slug,
              id: primaryData.mostReactedMessage?.id || "",
            })}
            interactive
          >
            <Spread space="sm" align="center">
              <span className="flex min-w-0 flex-1 flex-col gap-space-xs">
                <span className="text-overline-emphasis text-text-secondary">
                  {t("component.analytics-widget.community-feed-insights.most-reacted-post.label")}
                </span>
                <p className="truncate text-body-emphasis">{primaryData.mostReactedMessage?.title ?? "-"}</p>
              </span>
              {primaryData.mostReactedMessage && (
                <ReactionCountLabel reactions={primaryData.mostReactedMessage.totalReactionCountByType} />
              )}
            </Spread>
          </Card>

          {/* Most commented post */}
          <Card
            as={Link}
            to={routes.communityFeed.postDetails({
              slug,
              id: primaryData.mostCommentedMessage?.id || "",
            })}
            interactive
          >
            <Spread space="sm" align="center">
              <span className="flex min-w-0 flex-1 flex-col gap-space-xs">
                <span className="text-overline-emphasis text-text-secondary">
                  {t("component.analytics-widget.community-feed-insights.most-commented-post.label")}
                </span>
                <p className="truncate text-body-emphasis">{primaryData.mostCommentedMessage?.title ?? "-"}</p>
              </span>
              {primaryData.mostCommentedMessage && (
                <CommentCountLabel amountComments={primaryData.mostCommentedMessage.totalCommentCount} />
              )}
            </Spread>
          </Card>
        </Stack>
      </AnalyticsWidgetCard.Body>
      <AnalyticsWidgetCard.Footer>
        <Stack gap="sm">
          <Divider />
          <aside className="flex w-full items-center justify-end gap-space-sm text-text-secondary">
            <Tooltip tooltip={t("component.analytics-widget.community-feed-insights.tooltip.last-updated")}>
              <time className="whitespace-nowrap">
                {t("common.label.last-updated", {
                  distance: formatDistance(t, {
                    start: communityFeedStats?.lastUpdatedAt ? new Date(communityFeedStats.lastUpdatedAt) : clockDate,
                    end: clockDate,
                  }),
                })}
              </time>
            </Tooltip>
          </aside>
        </Stack>
      </AnalyticsWidgetCard.Footer>
    </AnalyticsWidgetCard.Root>
  );
}
type ReactionCountLabelProps = {
  reactions: MessageDetailV2Dto["totalReactionCountByType"];
};
const ReactionCountLabel = ({ reactions }: ReactionCountLabelProps) => {
  const { t } = useTranslation();
  const topReactions = Object.entries(reactions)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map((entry) => entry[0]);

  return (
    <span className="flex h-fit min-h-control-md items-center gap-space-sm rounded-md bg-background-secondary px-space-sm">
      <Inline gap="xs" align="center">
        {topReactions.map((reactionType) => (
          <img
            key={reactionType}
            src={getReactionImage(reactionType)}
            alt={getReactionLabel(reactionType, t)}
            className="h-icon-control"
          />
        ))}
      </Inline>
      <span className="text-body text-text-primary">{getTotalReactionAmount(reactions)}</span>
    </span>
  );
};
type CommentCountLLabelProps = {
  amountComments: number;
};
const CommentCountLabel = ({ amountComments }: CommentCountLLabelProps) => {
  return (
    <span className="flex h-fit min-h-control-md items-center gap-space-sm rounded-md bg-background-secondary px-space-sm text-text-primary">
      <MessageSquare02Icon size={20} />
      {amountComments}
    </span>
  );
};
